/**
 * Declarative schema of a public registration form.
 *
 * WHY: the invite forms used to be hardcoded per client on the old Nuxt front (a page
 * component per link). This describes them as data instead, so the backend can eventually
 * serve a schema from the admin panel — adding, removing or reordering fields, and pointing a
 * select at a new reference endpoint — with no front-end change.
 *
 * WHERE IT COMES FROM (in order):
 *   1. invite.form_data.registration_form.schema  — the backend/admin form, once implemented
 *   2. BUILTIN_SCHEMAS below                      — today's hardcoded forms, keyed by slug
 *   3. null                                       — the plain built-in registration form
 *
 * The two built-ins are the old Nuxt forms transcribed field by field; they are the reference
 * implementation of the contract and can be deleted once the backend serves the same JSON.
 *
 * WHAT THE BACKEND SENDS: see docs/architecture/registration-form-schema.md — that file is the
 * contract to hand to the backend developer.
 */

import type { ReferenceRow } from "@/api/register/registerReferencePublicApi";

export const REGISTRATION_SCHEMA_VERSION = 1;

/** Localized label of a reference row: which column to read per UI locale. */
export type ReferenceLabel = {
  /** Column used by default (and for ru/en). */
  default: string;
  /** Per-locale override, e.g. { kk: "name_kz" }. */
  byLocale?: Record<string, string>;
};

export type FieldSource =
  | {
      kind: "endpoint";
      /** Path under /api, e.g. "astana-daryn/districts.json". */
      path: string;
      /** Row column used as the submitted value. */
      value: string;
      label: ReferenceLabel;
      /** Query param filled with the parent field's value, e.g. "region". */
      param?: string;
    }
  | {
      kind: "dataset";
      /** Bundled static reference shipped with the front, e.g. "shygys". */
      dataset: string;
      /** Collection inside it, e.g. "districts" | "settlements" | "schools". */
      collection: string;
    };

export type TextField = {
      type: "text" | "password" | "tel" | "digits";
      /** Stable id, used for dependencies and internal state. */
      key: string;
      /** Key in the POST body. */
      payloadKey: string;
      placeholderKey: string;
      required?: boolean;
      /** Max length for `digits` (IIN is 12). */
      maxLength?: number;
      /** Yellow background, as the old forms marked credentials. */
      highlight?: boolean;
      defaultValue?: string;
};

export type SelectField = {
      type: "select";
      key: string;
      payloadKey: string;
      placeholderKey: string;
      required?: boolean;
      /** Inline options; `label` is literal text, `labelKey` goes through i18n. */
      options: Array<{ value: string; label?: string; labelKey?: string }>;
      defaultValue?: string;
};

export type ReferenceField = {
      type: "reference";
      key: string;
      payloadKey: string;
      placeholderKey: string;
      required?: boolean;
      searchable?: boolean;
      source: FieldSource;
      /** Key of the field this one cascades from; cleared whenever the parent changes. */
      dependsOn?: string;
      /** Hide until the parent has a value (old forms only showed the next level then). */
      hideUntilParent?: boolean;
};

export type SchemaField = TextField | SelectField | ReferenceField;

export type RegistrationFormSchema = {
  version: number;
  /**
   * Controller group for submit/validate:
   *   "almaty-daryn" -> POST /api/almaty-daryn/register/{validate,submit}.json
   *   null           -> POST /api/register/{validate,submit}.json
   */
  apiGroup: string | null;
  /** Sent with every submission as-is (flags and per-client constants). */
  constants: Record<string, unknown>;
  fields: SchemaField[];
  /** Field keys rendered side by side, e.g. [["iin", "phone"]]. */
  rows?: string[][];
  /**
   * «Забыли пароль?» под формой: сброс по ИИН и Ф.И.О. Только для форм, чьи аккаунты
   * заводятся без телефона и почты, — бэк сверяет Ф.И.О. лишь у таких.
   */
  fullnamePasswordReset?: boolean;
};

/* ------------------------------------------------------------------ *
 * Built-ins: the old Nuxt forms, transcribed.
 * ------------------------------------------------------------------ */

/**
 * astana-daryn для ПРЕПОДАВАТЕЛЕЙ — ссылка ПедОлимпа.
 * Cascade region -> district -> school comes from the API and is keyed by Russian names.
 */
const ASTANA_DARYN_SCHEMA: RegistrationFormSchema = {
  version: REGISTRATION_SCHEMA_VERSION,
  apiGroup: "astana-daryn",
  constants: {
    agree: true,
    patronymic: null,
    recaptcha_token: null,
    astana_daryn_teacher_name: "none",
    astana_daryn_select_lang: "русс",
  },
  fields: [
    {
      type: "reference",
      key: "region",
      payloadKey: "astana_daryn_region",
      placeholderKey: "placeholder-select-region",
      required: true,
      searchable: true,
      source: {
        kind: "endpoint",
        path: "astana-daryn/regions.json",
        value: "region_ru",
        label: { default: "region_ru", byLocale: { kk: "region_kz" } },
      },
    },
    {
      type: "reference",
      key: "district",
      payloadKey: "astana_daryn_district",
      placeholderKey: "placeholder-select-district",
      required: true,
      searchable: true,
      dependsOn: "region",
      hideUntilParent: true,
      source: {
        kind: "endpoint",
        path: "astana-daryn/districts.json",
        value: "district_ru",
        label: { default: "district_ru", byLocale: { kk: "district_kz" } },
        param: "region",
      },
    },
    {
      type: "reference",
      key: "school",
      payloadKey: "astana_daryn_school_id",
      placeholderKey: "placeholder-select-school",
      required: true,
      searchable: true,
      dependsOn: "district",
      hideUntilParent: true,
      source: {
        kind: "endpoint",
        path: "astana-daryn/schools.json",
        value: "id",
        label: { default: "name_ru", byLocale: { kk: "name_kz" } },
        param: "district",
      },
    },
    {
      /* Направление пишется в поле класса: по нему кабинет отбирает задания (useDarynClassFilter). */
      type: "select",
      key: "class",
      payloadKey: "astana_daryn_class_name",
      placeholderKey: "placeholder-select-direction",
      required: true,
      options: [
        { value: "2,4", labelKey: "label-class-range-2-4" },
        { value: "5,11", labelKey: "label-class-range-5-11" },
      ],
    },
    { type: "text", key: "lastname", payloadKey: "lastname", placeholderKey: "label-lastname", required: true },
    { type: "text", key: "firstname", payloadKey: "firstname", placeholderKey: "label-firstname", required: true },
    { type: "text", key: "patronymic", payloadKey: "patronymic", placeholderKey: "label-patronymic" },
    { type: "digits", key: "iin", payloadKey: "username", placeholderKey: "label-iin", required: true, maxLength: 12, highlight: true },
    { type: "tel", key: "phone", payloadKey: "phone", placeholderKey: "placeholder-invite-code-recipient-phone", required: true },
    { type: "password", key: "password", payloadKey: "password", placeholderKey: "placeholder-password", required: true, highlight: true },
  ],
  rows: [["iin", "phone"]],
};

/**
 * Направление, которое ссылка руководителей ПедОлимпа пишет в astana_daryn_class_name.
 * По нему кабинет отбирает задания (useDarynClassFilter), а профиль прячет пустую школу.
 */
export const ASTANA_DARYN_ADMIN_DIRECTION = "Руководители и пед. работники";

/**
 * astana-daryn для РУКОВОДИТЕЛЕЙ — ПедОлимп для руководителей, административно-методических
 * и других педагогических работников.
 *
 * Контракт:
 *  - форма учителей без района, школы и направления: участник выбирает только регион
 *    (области и города республиканского значения);
 *  - бэкенд требует astana_daryn_school_id числом, поэтому уходит 0: школы нет, на её месте
 *    в документах печатается организация (CertificateOrganizationDrawer);
 *  - направление фиксировано: задания по категориям, кроме «Мектептің …» и «ТжКБ …».
 */
const ASTANA_DARYN_ADMIN_SCHEMA: RegistrationFormSchema = {
  ...ASTANA_DARYN_SCHEMA,
  constants: {
    ...ASTANA_DARYN_SCHEMA.constants,
    astana_daryn_school_id: 0,
    astana_daryn_class_name: ASTANA_DARYN_ADMIN_DIRECTION,
  },
  fields: ASTANA_DARYN_SCHEMA.fields.filter(
    (field) => !["district", "school", "class"].includes(field.key),
  ),
};

/**
 * astana-daryn для УЧЕНИКОВ — олимпиада «ШЫҢ» и мартовская ссылка.
 *
 * Тот же контроллер и тот же каскад регион → район → школа, что у ПедОлимпа, но набор полей
 * другой: есть ФИО преподавателя, классы идут по одному со 2 по 11, и нет отчества с телефоном.
 */
const ASTANA_DARYN_STUDENT_SCHEMA: RegistrationFormSchema = {
  version: REGISTRATION_SCHEMA_VERSION,
  apiGroup: "astana-daryn",
  constants: {
    agree: true,
    patronymic: null,
    /* Ключ phone не шлём совсем: на присутствующем ключе бэк чистит значение регуляркой и
       кладёт пустую строку вместо NULL, а восстановление доступа по Ф.И.О. ищет строго
       whereNull('phone'). Без ключа в базу уходит NULL и участник может вернуться в аккаунт. */
    recaptcha_token: null,
    astana_daryn_select_lang: "русс",
  },
  fields: [
    {
      type: "reference",
      key: "region",
      payloadKey: "astana_daryn_region",
      placeholderKey: "placeholder-select-region",
      required: true,
      searchable: true,
      source: {
        kind: "endpoint",
        path: "astana-daryn/regions.json",
        value: "region_ru",
        label: { default: "region_ru", byLocale: { kk: "region_kz" } },
      },
    },
    {
      type: "reference",
      key: "district",
      payloadKey: "astana_daryn_district",
      placeholderKey: "placeholder-select-district",
      required: true,
      searchable: true,
      dependsOn: "region",
      hideUntilParent: true,
      source: {
        kind: "endpoint",
        path: "astana-daryn/districts.json",
        value: "district_ru",
        label: { default: "district_ru", byLocale: { kk: "district_kz" } },
        param: "region",
      },
    },
    {
      type: "reference",
      key: "school",
      payloadKey: "astana_daryn_school_id",
      placeholderKey: "placeholder-select-school",
      required: true,
      searchable: true,
      dependsOn: "district",
      hideUntilParent: true,
      source: {
        kind: "endpoint",
        path: "astana-daryn/schools.json",
        value: "id",
        label: { default: "name_ru", byLocale: { kk: "name_kz" } },
        param: "district",
      },
    },
    {
      /* Бэкенд группы astana-daryn требует это поле: без него validate.json отдаёт 400. */
      type: "text",
      key: "teacher",
      payloadKey: "astana_daryn_teacher_name",
      placeholderKey: "placeholder-teacher-name",
      required: true,
    },
    {
      /* Классы со 2-го: олимпиада не проводится для первого. */
      type: "select",
      key: "class",
      payloadKey: "astana_daryn_class_name",
      placeholderKey: "placeholder-select-class",
      required: true,
      options: Array.from({ length: 10 }, (_, i) => ({
        value: String(i + 2),
        labelKey: "ui-class-number",
      })),
    },
    { type: "text", key: "lastname", payloadKey: "lastname", placeholderKey: "label-lastname-participants", required: true },
    { type: "text", key: "firstname", payloadKey: "firstname", placeholderKey: "label-firstname-participants", required: true },
    { type: "digits", key: "iin", payloadKey: "username", placeholderKey: "label-iin", required: true, maxLength: 12, highlight: true },
    { type: "password", key: "password", payloadKey: "password", placeholderKey: "placeholder-password", required: true, highlight: true },
  ],
  fullnamePasswordReset: true,
};

/**
 * almaty-daryn + shygys — запасная форма для прочих «daryny»-ссылок.
 *
 * Живых ссылок на ней сейчас нет: «ШЫҢ» переведена на каскад по регионам. Схема оставлена
 * как поведение старого фронта для неизвестных слагов и как основа для vko-daryny.
 * Its cascade comes from a static file bundled with the front (public/data/shygys.json),
 * not from the API, so its sources are `dataset` rather than `endpoint`.
 */
const ALMATY_DARYN_SHYGYS_SCHEMA: RegistrationFormSchema = {
  version: REGISTRATION_SCHEMA_VERSION,
  apiGroup: "almaty-daryn",
  constants: {
    agree: true,
    patronymic: null,
    /* Как и в схеме учеников astana-daryn: ключ phone не отправляем, иначе в базе окажется
       пустая строка и восстановление доступа по Ф.И.О. такой аккаунт не найдёт. */
    recaptcha_token: null,
    shygys_school_id: null,
    aqmo_modo_school_id: null,
    cpfed_school_name: null,
    cpfed_class_name: null,
    cpfed_region_name: null,
    cpfed_city_name: null,
    cpfed_parent_name: null,
    cpfed_parent_phone: null,
    cpfed_promocode: null,
  },
  fields: [
    {
      type: "reference",
      key: "district",
      payloadKey: "shygys_district_id",
      placeholderKey: "placeholder-select-district",
      required: true,
      searchable: true,
      source: { kind: "dataset", dataset: "shygys", collection: "districts" },
    },
    {
      type: "reference",
      key: "settlement",
      payloadKey: "shygys_settlement_id",
      placeholderKey: "placeholder-select-settlement",
      required: true,
      searchable: true,
      dependsOn: "district",
      hideUntilParent: true,
      source: { kind: "dataset", dataset: "shygys", collection: "settlements" },
    },
    {
      /* The school select feeds almaty_daryn_school_id, not shygys_school_id — that is how the
         old form is wired and how the backend resolves the school. */
      type: "reference",
      key: "school",
      payloadKey: "almaty_daryn_school_id",
      placeholderKey: "placeholder-select-school",
      required: true,
      searchable: true,
      dependsOn: "settlement",
      hideUntilParent: true,
      source: { kind: "dataset", dataset: "shygys", collection: "schools" },
    },
    { type: "text", key: "teacher", payloadKey: "almaty_daryn_teacher_name", placeholderKey: "placeholder-teacher-name" },
    {
      type: "select",
      key: "class",
      payloadKey: "almaty_daryn_class_name",
      placeholderKey: "placeholder-select-class",
      required: true,
      options: Array.from({ length: 11 }, (_, i) => ({
        value: String(i + 1),
        labelKey: "ui-class-number",
      })),
    },
    {
      type: "select",
      key: "lang",
      payloadKey: "almaty_daryn_select_lang",
      placeholderKey: "placeholder-select-lang",
      required: true,
      defaultValue: "русс",
      options: [
        { value: "қаз", label: "Қазақша" },
        { value: "русс", label: "Русский" },
      ],
    },
    { type: "text", key: "lastname", payloadKey: "lastname", placeholderKey: "label-lastname-participants", required: true },
    { type: "text", key: "firstname", payloadKey: "firstname", placeholderKey: "label-firstname-participants", required: true },
    { type: "digits", key: "iin", payloadKey: "username", placeholderKey: "label-iin", required: true, maxLength: 12, highlight: true },
    { type: "password", key: "password", payloadKey: "password", placeholderKey: "placeholder-password", required: true, highlight: true },
  ],
};

/**
 * Slug -> built-in schema. This map is the ONLY hardcoded part left, and it exists purely
 * because the invite carries no flavour marker yet: GET /api/invite-code/by-url is whitelisted
 * down to code, form_data, form_description, form_logo, form_name, form_url — and form_data
 * comes back empty for these invites. Once the backend serves a schema, this map stops being
 * consulted for that link.
 */
const BUILTIN_SCHEMAS: Record<string, RegistrationFormSchema> = {
  "astana-daryny-pedolimpastdrn2026": ASTANA_DARYN_SCHEMA,
  "astana-daryny-admin2026": ASTANA_DARYN_ADMIN_SCHEMA,
  "astana-daryny-2026-03-01": ASTANA_DARYN_STUDENT_SCHEMA,
  "astana-daryny-2026-07-09": ASTANA_DARYN_STUDENT_SCHEMA,
};

/** Slugs that had their own page on the old front which is not ported yet. */
const NOT_PORTED_SLUGS = new Set(["vko-daryny", "almaty-daryn"]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

/** Reads a backend-served schema out of the invite, if there is one. */
export function readSchemaFromInvite(formData: unknown): RegistrationFormSchema | null {
  if (!isRecord(formData)) return null;

  const nested = isRecord(formData["registration_form"])
    ? (formData["registration_form"] as Record<string, unknown>)
    : null;

  for (const source of [formData, nested]) {
    const candidate = source?.["schema"];
    if (isRecord(candidate) && Array.isArray(candidate["fields"])) {
      return candidate as unknown as RegistrationFormSchema;
    }
  }

  return null;
}

export function resolveRegistrationSchema(
  formUrl: string | null | undefined,
  slug: string,
  formData?: unknown,
): RegistrationFormSchema | null {
  const served = readSchemaFromInvite(formData);
  if (served) return served;

  const source = ((formUrl ?? "").trim() || slug).trim().toLowerCase();

  const builtin = BUILTIN_SCHEMAS[source];
  if (builtin) return builtin;

  if (NOT_PORTED_SLUGS.has(source)) return null;

  return source.includes("daryny") ? ALMATY_DARYN_SHYGYS_SCHEMA : null;
}

/* ------------------------------------------------------------------ *
 * Helpers shared by the renderer and the payload builder.
 * ------------------------------------------------------------------ */

export function pickReferenceLabel(
  row: ReferenceRow,
  label: ReferenceLabel,
  locale: string,
): string {
  const column = label.byLocale?.[locale] ?? label.default;
  const localized = row[column];
  if (typeof localized === "string" && localized.trim()) return localized;

  const fallback = row[label.default];
  return typeof fallback === "string" ? fallback : String(fallback ?? "");
}

export function fieldValueIsFilled(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  return true;
}

/* ------------------------------------------------------------------ *
 * Submission
 * ------------------------------------------------------------------ */

export type SchemaFormValues = Record<string, string | number | null>;

/** Starting values: every field empty, except the ones the schema pre-fills. */
export function initialSchemaValues(schema: RegistrationFormSchema): SchemaFormValues {
  return Object.fromEntries(
    schema.fields.map((field) => [
      field.key,
      field.type === "reference" ? null : (field.defaultValue ?? ""),
    ]),
  );
}

/**
 * Body of both validate.json and submit.json: the schema's constants, then one entry per
 * field. Empty text becomes null — the backend treats "" and null differently.
 */
export function buildSchemaPayload(
  schema: RegistrationFormSchema,
  values: SchemaFormValues,
  inviteCode: string,
): { invite_code: string } & Record<string, unknown> {
  const payload = { ...schema.constants, invite_code: inviteCode } as { invite_code: string } & Record<string, unknown>;

  for (const field of schema.fields) {
    const value = values[field.key];
    payload[field.payloadKey] = typeof value === "string" ? (value.trim() || null) : (value ?? null);
  }

  return payload;
}

/** Send is enabled once every required field has a value (old front: `sendAvailable`). */
export function isSchemaFormFilled(
  schema: RegistrationFormSchema,
  values: SchemaFormValues,
): boolean {
  return schema.fields.every(
    (field) => !field.required || fieldValueIsFilled(values[field.key]),
  );
}

/** Keys of the fields that cascade from the given one, directly or transitively. */
export function dependentFieldKeys(schema: RegistrationFormSchema, key: string): string[] {
  const direct = schema.fields
    .filter((field) => field.type === "reference" && field.dependsOn === key)
    .map((field) => field.key);

  return direct.flatMap((childKey) => [childKey, ...dependentFieldKeys(schema, childKey)]);
}
